import React, { useState, useEffect } from 'react';
import { ClerkProvider, SignIn, SignUp, RedirectToSignIn, SignedIn, SignedOut, UserButton, useAuth, useUser } from '@clerk/clerk-react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { ReactComponent as RuniumIcon } from './assets/runium_icon.svg';
import Dashboard from './components/Dashboard';
import AdminFeedbackPage from './admin/feedback/AdminFeedbackPage';
import TrainingRulesAdmin from './admin/training-rules/TrainingRulesAdmin';
import EvaluationDashboard from './admin/evaluation/EvaluationDashboard';
import EvaluationDashboardBackup from './admin/evaluation/EvaluationDashboardBackup';
import TestBackup from './admin/evaluation/TestBackup';
import MultisessionAppSupport from './components/MultisessionAppSupport';
import ChatManager from './components/ChatManager';
import AuthCallback from './components/AuthCallback';
import SubscriptionGate from './components/SubscriptionGate';
import { FEATURE_FLAGS } from './config/featureFlags';
import { openLegalUrl } from './utils/legalUtils';
import { clerkTokenCache } from './utils/clerkTokenCache';

// Import your publishable key
const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || 'pk_test_c21vb3RoLXByaW1hdGUtMjcuY2xlcmsuYWNjb3VudHMuZGV2JA';

// Shared dark theme for Clerk auth components (sign-in & sign-up pages)
const clerkDarkTheme = {
  variables: {
    colorBackground: 'transparent',
    colorText: '#f9fafb',
    colorTextSecondary: '#d1d5db',
    colorTextOnPrimaryBackground: '#ffffff',
    colorInputBackground: '#374151',
    colorInputText: '#f9fafb',
    colorPrimary: '#3b82f6',
    colorDanger: '#ef4444',
    colorSuccess: '#22c55e',
    colorWarning: '#eab308',
    colorNeutral: '#d1d5db',
    borderRadius: '0.5rem',
  },
  elements: {
    rootBox: 'w-full',
    card: 'w-full border-0 shadow-none bg-transparent',
    headerTitle: 'text-white',
    headerSubtitle: 'text-gray-300',
    socialButtonsBlockButton: 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600',
    formFieldInput: 'bg-gray-700 border-gray-600 text-white',
    formFieldLabel: 'text-gray-200',
    footerActionLink: 'text-blue-400 hover:text-blue-300',
    footerActionText: 'text-gray-400',
    dividerText: 'text-gray-400',
    dividerLine: 'bg-gray-600',
    formFieldSuccessText: 'text-green-400',
    formFieldInfoText: 'text-gray-300',
    formFieldWarningText: 'text-yellow-400',
    formFieldErrorText: 'text-red-400',
    alertText: 'text-red-300',
    formFieldAction: 'text-blue-400 hover:text-blue-300',
    identityPreviewText: 'text-white',
    identityPreviewEditButton: 'text-blue-400',
    formFieldHintText: 'text-gray-400',
    footer: 'text-gray-500',
  },
};

// Loading spinner component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      <div className="w-8 h-8 border-2 border-blue-400/30 rounded-full"></div>
      <div className="absolute top-0 left-0 w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </div>
);

// Enhanced loading component with pulsing dots
const TypingIndicator = () => (
  <div className="flex items-center justify-center space-x-1">
    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
  </div>
);

// Age Verification Modal Component
const AgeVerificationModal = ({ onVerified, onCancel }) => {
  const [ageVerified, setAgeVerified] = useState(false);
  const [medicalDisclaimerAccepted, setMedicalDisclaimerAccepted] = useState(false);

  const handleContinue = () => {
    if (ageVerified && medicalDisclaimerAccepted) {
      // Store in localStorage to send to backend after sign-up
      localStorage.setItem('legal_acknowledgments', JSON.stringify({
        age_verified: ageVerified,
        medical_disclaimer_accepted: medicalDisclaimerAccepted,
        timestamp: new Date().toISOString()
      }));
      onVerified();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-gray-700/70 max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-blue-500/10 px-6 py-4 border-b border-gray-700/50">
          <h2 className="text-lg sm:text-xl font-bold text-white text-center">
            Age Verification & Medical Disclaimer
          </h2>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Age Verification */}
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50 hover:border-blue-500/50 transition-colors">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={ageVerified}
                onChange={(e) => setAgeVerified(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 focus:ring-offset-gray-800"
              />
              <span className="text-sm sm:text-base text-gray-200 flex-1">
                I confirm that I am 18 years of age or older
              </span>
            </label>
          </div>

          {/* Medical Disclaimer */}
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50 hover:border-blue-500/50 transition-colors">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={medicalDisclaimerAccepted}
                onChange={(e) => setMedicalDisclaimerAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 focus:ring-offset-gray-800"
              />
              <span className="text-sm sm:text-base text-gray-200 flex-1">
                I understand that Runium provides AI-powered fitness coaching and is not a substitute for professional medical advice, diagnosis, or treatment. I will consult with a healthcare provider before starting any new fitness program.
              </span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium transition-colors text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              onClick={handleContinue}
              disabled={!ageVerified || !medicalDisclaimerAccepted}
              className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-medium transition-colors text-sm sm:text-base"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Full screen loading overlay for authentication transitions
const AuthLoadingOverlay = ({ message = "Initializing...", debugInfo = [] }) => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900 text-white px-4">
    <div className="mb-8 text-center">
      <RuniumIcon className="w-16 h-16 mx-auto mb-4 text-blue-400 filter drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
      <h2 className="text-xl font-semibold text-blue-400 mb-2">Runium</h2>
    </div>
    
    <div className="flex flex-col items-center space-y-4">
      <LoadingSpinner />
      <p className="text-gray-300 text-sm">{message}</p>
      <TypingIndicator />
    </div>

    {/* Debug info panel — only in development */}
    {process.env.NODE_ENV === 'development' && debugInfo && debugInfo.length > 0 && (
      <div className="mt-8 w-full max-w-md">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <h3 className="text-xs font-semibold text-gray-400 mb-2">Debug Info:</h3>
          <div className="space-y-1">
            {debugInfo.map((info, index) => (
              <p key={index} className="text-xs text-gray-300 font-mono">
                {info}
              </p>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
);

// Custom welcome component with runner theme and loading state
const Welcome = () => {
  // No artificial delays — Clerk's <SignIn> renders immediately

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-900 text-white p-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="mb-8 text-center">
        <RuniumIcon className="w-24 h-24 mx-auto mb-5 text-blue-400 filter drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600 mb-2">Welcome to Runium</h1>
        <p className="mt-2 text-gray-300 text-lg">Your personal AI running coach</p>
      </div>
      
      {/* Container with proper centering and loading states */}
      <div className="w-full max-w-md flex flex-col items-center justify-center min-h-[400px]">
        <div className="bg-gradient-to-b from-gray-800 to-gray-800/80 rounded-2xl shadow-xl overflow-hidden border border-gray-700/70 backdrop-filter backdrop-blur-sm w-full">
          <div>
              <div className="p-0">
                {/* Ensure the SignIn component fills the container properly */}
                <div className="clerk-auth-container">
                  <SignIn
                    routing="path"
                    path="/sign-in"
                    signUpUrl="/sign-up"
                    appearance={clerkDarkTheme}
                  />
                </div>
              </div>
            </div>
        </div>
        {FEATURE_FLAGS.LEGAL_LINKS && (
          <div className="mt-6 sm:mt-8 pb-4 sm:pb-6">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-400">
              <button
                onClick={() => openLegalUrl('https://runium.ai/privacy')}
                className="hover:text-blue-400 transition-colors"
              >
                Privacy Policy
              </button>
              <span className="text-gray-600">•</span>
              <button
                onClick={() => openLegalUrl('https://runium.ai/terms')}
                className="hover:text-blue-400 transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Custom signup component with the same improvements
const SignUpPage = () => {
  // No artificial delays — Clerk's <SignUp> renders immediately
  const [showAgeVerification, setShowAgeVerification] = useState(FEATURE_FLAGS.AGE_VERIFICATION);

  const handleAgeVerificationComplete = () => {
    setShowAgeVerification(false);
  };

  const handleAgeVerificationCancel = () => {
    // Redirect to sign-in if user cancels
    window.location.href = '/sign-in';
  };

  return (
    <>
      {FEATURE_FLAGS.AGE_VERIFICATION && showAgeVerification && (
        <AgeVerificationModal
          onVerified={handleAgeVerificationComplete}
          onCancel={handleAgeVerificationCancel}
        />
      )}
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-900 text-white p-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-y-auto">
      <div className="mb-8 text-center">
        <RuniumIcon className="w-24 h-24 mx-auto mb-5 text-blue-400 filter drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600 mb-2">Join Runium</h1>
        <p className="mt-2 text-gray-300 text-lg">Create your account to get started</p>
      </div>
      
      {/* Container with proper centering and loading states */}
      <div className="w-full max-w-md flex flex-col items-center justify-center min-h-[400px]">
        <div className="bg-gradient-to-b from-gray-800 to-gray-800/80 rounded-2xl shadow-xl overflow-hidden border border-gray-700/70 backdrop-filter backdrop-blur-sm w-full">
          <div>
              <div className="p-0">
                <div className="clerk-auth-container">
                  <SignUp
                    routing="path"
                    path="/sign-up"
                    signInUrl="/sign-in"
                    appearance={clerkDarkTheme}
                  />
                </div>
              </div>
            </div>
        </div>
        {FEATURE_FLAGS.LEGAL_LINKS && (
          <div className="mt-4 pt-4 border-t border-gray-700/50 px-6 pb-3">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs text-gray-400">
              <span className="text-gray-500 text-center w-full sm:w-auto mb-1 sm:mb-0">By signing up, you agree to our:</span>
              <button
                onClick={() => openLegalUrl('https://runium.ai/privacy')}
                className="hover:text-blue-400 transition-colors whitespace-nowrap"
              >
                Privacy Policy
              </button>
              <span className="text-gray-600 hidden sm:inline">•</span>
              <button
                onClick={() => openLegalUrl('https://runium.ai/terms')}
                className="hover:text-blue-400 transition-colors whitespace-nowrap"
              >
                Terms of Service
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

// Enhanced Auth Guard component that handles loading states gracefully
const AuthGuard = ({ children }) => {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded: userLoaded } = useUser();
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // Track elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(e => e + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Set a timeout for Clerk initialization
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!authLoaded || !userLoaded) {
        console.error('Clerk initialization timeout - forcing redirect to sign-in');
        setTimeoutReached(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, [authLoaded, userLoaded]);

  // Collect debug information
  const debugInfo = [
    `Platform: ${Capacitor.isNativePlatform() ? 'Native (' + Capacitor.getPlatform() + ')' : 'Web'}`,
    `Clerk Auth Loaded: ${authLoaded}`,
    `Clerk User Loaded: ${userLoaded}`,
    `Is Signed In: ${isSignedIn}`,
    `Elapsed Time: ${elapsed}s`,
    `Timeout Reached: ${timeoutReached}`,
    `Publishable Key: ${PUBLISHABLE_KEY.substring(0, 20)}...`
  ];

  // If timeout reached and still not loaded, force redirect to sign-in
  if (timeoutReached && (!authLoaded || !userLoaded)) {
    console.log('Clerk failed to load, redirecting to sign-in page');
    return <Navigate to="/sign-in" replace />;
  }

  // Show loading overlay while Clerk is initializing or transitioning between states
  if (!authLoaded || !userLoaded) {
    return <AuthLoadingOverlay message="Authenticating..." debugInfo={debugInfo} />;
  }

  // If loaded but not signed in, navigate to our in-app sign in page
  // Use Navigate instead of RedirectToSignIn to stay within the app (important for Capacitor)
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  // User is authenticated and everything is loaded
  return children;
};

function App() {
  // Global viewport height handler for mobile - sets CSS custom property
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    const setViewportHeight = () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;
      // Use visualViewport if available (most accurate on mobile), otherwise window.innerHeight
      const vh = window.visualViewport?.height || window.innerHeight;
      // Set CSS custom property for viewport height
      document.documentElement.style.setProperty('--vh', `${vh * 0.01}px`);
    };
    
    // Set initial value
    setViewportHeight();
    
    // Update on resize (keyboard open/close, orientation change, etc.)
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    
    // Use visualViewport API if available (more accurate for mobile keyboard)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setViewportHeight);
      window.visualViewport.addEventListener('scroll', setViewportHeight);
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', setViewportHeight);
        window.removeEventListener('orientationchange', setViewportHeight);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', setViewportHeight);
          window.visualViewport.removeEventListener('scroll', setViewportHeight);
        }
      }
    };
  }, []);
  
  // Detect if running in Capacitor (native app)
  const isNative = Capacitor.isNativePlatform();

  // Configure ClerkProvider based on platform
  const clerkConfig = {
    publishableKey: PUBLISHABLE_KEY,
    afterMultiSessionSingleSignOutUrl: "/",
    signInFallbackRedirectUrl: "/",
    signUpFallbackRedirectUrl: "/",
  };

  // Add tokenCache for native platforms (required for Capacitor)
  if (isNative) {
    clerkConfig.tokenCache = clerkTokenCache;
  }

  return (
    <ClerkProvider {...clerkConfig}>
      <ChatManager.ChatManagerProvider>
        <SubscriptionGate>
        <Router>
          <MultisessionAppSupport>
            <Routes>
              <Route path="/auth-callback" element={<AuthCallback />} />
              <Route path="/sign-in/*" element={<Welcome />} />
              <Route path="/sign-up/*" element={<SignUpPage />} />
              <Route
                path="/feedback"
                element={
                  <AuthGuard>
                    <Navigate to="/?feedback" replace />
                  </AuthGuard>
                }
              />
              <Route
                path="/"
                element={
                  <AuthGuard>
                    <Dashboard />
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/feedback"
                element={
                  <AuthGuard>
                    <AdminFeedbackPage />
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/training-rules"
                element={
                  <AuthGuard>
                    <TrainingRulesAdmin />
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/evaluation/backup"
                element={
                  <AuthGuard>
                    <EvaluationDashboardBackup />
                  </AuthGuard>
                }
              />
              <Route
                path="/admin/evaluation"
                element={
                  <AuthGuard>
                    <EvaluationDashboard />
                  </AuthGuard>
                }
              />
            </Routes>
          </MultisessionAppSupport>
        </Router>
        </SubscriptionGate>
      </ChatManager.ChatManagerProvider>
    </ClerkProvider>
  );
}

export default App;
